import Tippy from '@tippyjs/react/headless/dist/tippy-react-headless.esm.js';
import classNames from 'classnames';
import type React from 'react';
import { forwardRef } from 'react';
import { followCursor as followCursorPlugin } from 'tippy.js';
import type { Size } from '../../constants';

export type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'auto'
  | 'auto-start'
  | 'auto-end';

type HideOnClick = true | false | 'toggle';

type TooltipShadow = Extract<Size, 'md' | 'lg' | 'xl'> | 'none';

export type TooltipBackground = 'light' | 'dark' | 'transparent' | 'primary';

export interface TooltipProps {
  /**
   * @param skidding displaces the tooltip along the reference element.
   * @param distance displaces the tooltip away from, or toward, the reference element in the direction of its placement.
   */
  offset?: [skidding: number, distance: number];
  /**
   * @param show delay in milliseconds when the tooltip opens.
   * @param hide delay in milliseconds when the tooltip closes.
   */
  delay?: [show: number, hide: number];
  /**
   * Preferred placement of the tooltip.
   */
  placement: Placement;
  /**
   * Determines if the tooltip has interactive content inside of it, so that it can be hovered over and clicked inside without hiding.
   */
  interactive?: boolean;
  /**
   * Determines if the tooltip follows the user's mouse cursor.
   */
  followCursor?: boolean;
  /**
   * Determines the events that cause the tooltip to show.
   */
  trigger?: 'mouseenter' | 'click';
  /**
   * Specifies the z-index CSS on the root popper node.
   */
  zIndex?: number;
  /**
   * Determines the size of the invisible border around the tooltip that will prevent it from hiding if the cursor left it.
   */
  interactiveBorder?: number;
  /**
   * Determines if the tooltip hides upon clicking the reference or outside of the tippy.
   * `true` - always hide upon clicking.
   * `false` - never hide upon clicking.
   * `toggle` - hide only upon clicking the reference, but not outside.
   */
  hideOnClick?: HideOnClick;
  /**
   * Reference element that the tooltip should append to.
   */
  anchorRef: React.RefObject<any>;
  /**
   * Invoked once the tooltip begins to show.
   */
  onShow?: () => void;
  /**
   * Invoked when the user clicks anywhere outside of the tooltip or reference element.
   */
  onClickOutside?: () => void;
  /**
   * Hide the tooltip arrow.
   */
  noArrow?: boolean;
  /**
   * Determines if the tooltip should be appended to a specific element.
   */
  appendTo?: HTMLElement | string | (() => HTMLElement);
  arrowDown?: boolean;
  visible?: boolean;
  arrowPosition?: 'center' | 'start' | 'end';
  autoCalcArrowPosition?: boolean;
  shadow?: TooltipShadow;
  background?: TooltipBackground;
  fixedWidth?: boolean;
  id?: string;
  children?: React.ReactNode;
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      visible,
      noArrow,
      arrowPosition = 'center',
      children,
      hideOnClick,
      anchorRef,
      trigger = 'mouseenter',
      arrowDown,
      interactive = true,
      autoCalcArrowPosition,
      shadow = 'xl',
      background = 'light',
      fixedWidth,
      interactiveBorder = 12,
      id,
      ...props
    },
    ref
  ) => {
    const renderTooltip = (attrs: any) => (
      <div
        ref={ref}
        className={classNames(
          'rounded-md max-w-max pointer-events-auto',
          {
            'w-80': fixedWidth,
          },
          {
            md: 'shadow-ds-lg',
            lg: 'drop-shadow-lg',
            xl: 'shadow-xl',
            none: 'shadow-none',
          }[shadow],
          {
            light: 'bg-ds-accent3-100',
            dark: 'bg-ds-natural-600',
            primary: 'bg-ds-primary-300',
            transparent: 'bg-transparent',
          }[background]
        )}
        {...attrs}
        id={id}
      >
        {!noArrow && (
          <div
            className={classNames('flex text-ds-accent3-100 mx-3', {
              'justify-start': arrowPosition === 'start',
              'justify-center': arrowPosition === 'center',
              'justify-end': arrowPosition === 'end',
            })}
          >
            <div
              data-popper-arrow={autoCalcArrowPosition}
              className={classNames(
                'absolute',
                arrowDown ? ['-bottom-4, rotate-180'] : '-top-4'
              )}
            >
              <svg
                className="cursor-pointer h-4"
                viewBox="0 0 255 255"
              >
                <polygon
                  className="fill-current"
                  points="0,255 127.5,127.5 255,255"
                />
              </svg>
            </div>
          </div>
        )}
        {children}
      </div>
    );

    return (
      <Tippy
        interactive={interactive}
        hideOnClick={visible === undefined ? hideOnClick : undefined}
        trigger={visible === undefined ? trigger : undefined}
        visible={visible}
        reference={anchorRef}
        render={renderTooltip}
        plugins={[followCursorPlugin]}
        interactiveBorder={interactiveBorder}
        onMount={({ popper }) => {
          popper.className = classNames(
            popper.className,
            'pointer-events-none'
          );
        }}
        {...props}
      />
    );
  }
);

Tooltip.displayName = 'Tooltip';
